/**
 * 1.6.0 AI content description: metadata suggestions on the asset page's Metadata tab (plan C2: Update metadata as a
 * suggestion, the describe_asset summary). Stream A's file (MetadataTab.tsx only mounts it): each pending suggestion
 * with the field, the suggested value beside the current one, who suggested it, and Accept / Reject
 * (useMetadataSuggestions, useDecideMetadataSuggestion; accepting is an edit by the person, so the form reloads).
 */
import { Check, Sparkles, X } from 'lucide-react';
import type { AssetDetail } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useDecideMetadataSuggestion, useMetadataSuggestions } from '../../api/hooks/suggestions';
import { toast } from '../../components/Toasts';
import { suggesterLabel } from '../log/Suggestions';

/** A stored value as text: lists joined, empty as a dash. */
export function valueText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(valueText).join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function MetadataSuggestions({ asset, canEdit }: { asset: AssetDetail; canEdit: boolean }) {
  const q = useMetadataSuggestions(asset.id);
  const decide = useDecideMetadataSuggestion();
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  async function act(id: string, decision: 'accept' | 'reject', label: string) {
    try {
      await decide.mutateAsync({ assetId: asset.id, id, decision });
      toast(decision === 'accept' ? `${label} set from the suggestion` : `Suggestion for ${label} rejected`, 'info');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <section className="ai-metadata-suggestions" aria-label="AI suggestions" data-testid="metadata-suggestions">
      <h4><Sparkles size={14} aria-hidden /> AI suggestion{items.length === 1 ? '' : 's'}</h4>
      {items.map((s) => (
        <div key={s.id} className="ai-metadata-suggestion" data-testid="metadata-suggestion" data-field={s.fieldId}>
          <div className="ai-metadata-suggestion-text">
            <strong>{s.fieldLabel}</strong>
            <span className="ai-suggested-value">{valueText(s.value)}</span>
            <span className="faint">Now: {valueText(s.current)} · suggested by {suggesterLabel(s.suggestedBy)}, {new Date(s.suggestedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
          </div>
          {canEdit && (
            <div className="ai-metadata-suggestion-actions">
              <button type="button" className="btn sm" disabled={decide.isPending} onClick={() => void act(s.id, 'accept', s.fieldLabel)} data-testid="metadata-suggestion-accept"><Check size={12} /> Accept</button>
              <button type="button" className="btn sm ghost danger" disabled={decide.isPending} onClick={() => void act(s.id, 'reject', s.fieldLabel)} data-testid="metadata-suggestion-reject"><X size={12} /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
